import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, usePageNavigation } from '@shared/components';
import { useNotificationPreferences, useUpdatePreference } from '@modules/Notifications/hooks';
import type { NotificationPreference } from '@modules/Notifications/models/Notification.types';
import { NotificationPreferencesTable } from '../display/NotificationPreferencesTable';

interface PreferenceState {
  inApp: boolean;
  email: boolean;
}

export const Main = () => {
  const { t } = useTranslation('notificationsModule');
  const { preferences, isLoading } = useNotificationPreferences();
  const { mutate: updatePreference, isPending } = useUpdatePreference();
  const [preferencesState, setPreferencesState] = useState<Record<number, PreferenceState>>({});

  const breadcrumbs = useMemo(() => [{ label: t('notifications.preferences.pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  useEffect(() => {
    if (preferences.length > 0) {
      const initial: Record<number, PreferenceState> = {};
      preferences
        .filter((pref) => pref.id != null)
        .forEach((pref: NotificationPreference) => {
          initial[pref.id] = {
            inApp: pref.enabled_in_app ?? false,
            email: pref.enabled_email ?? false,
          };
        });
      setPreferencesState(initial);
    }
  }, [preferences]);

  const visiblePreferences = useMemo(() => preferences.filter((pref) => pref.id != null), [preferences]);

  const handleToggle = (pref: NotificationPreference, channel: 'inApp' | 'email') => {
    const currentState = preferencesState[pref.id] ?? {
      inApp: pref.enabled_in_app ?? false,
      email: pref.enabled_email ?? false,
    };
    const newState: PreferenceState = {
      ...currentState,
      [channel]: !currentState[channel],
    };

    setPreferencesState((prev) => ({
      ...prev,
      [pref.id]: newState,
    }));

    updatePreference({
      user_notification_preference_id: pref.id,
      enabled_in_app: newState.inApp,
      enabled_email: newState.email,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16" data-testid="notification-preferences-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <NotificationPreferencesTable
      preferences={visiblePreferences}
      preferencesState={preferencesState}
      isPending={isPending}
      onToggle={handleToggle}
    />
  );
};
